import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MailPlus } from "lucide-react";
import { apiClient, ApiError, apiErrorMessage } from "@/api/client";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type CustomerUserRole = "customer_admin" | "customer_user";
type CustomerUserState = "active" | "disabled" | "deleted";

interface CustomerUser {
  customerUserId: string;
  displayName: string;
  email: string;
  role: CustomerUserRole;
  userState: CustomerUserState;
  deletedAt: string | null;
  createdAt: string;
}

interface InvitationSummary {
  invitationId: string;
  maskedRecipientEmail: string;
  role: CustomerUserRole;
  expiresAt: string;
  invitationState: "issuing" | "delivered" | "delivery_unknown" | "delivery_rejected" | "revoked" | "expired";
  createdAt: string;
}

const CUSTOMER_USERS_PATH = "/customer-users";
const INVITATIONS_PATH = "/console-bff/auth/current-customer/invitations";

export function UsersPage() {
  const { t } = useTranslation("admin");
  const { t: tc } = useTranslation();
  const [users, setUsers] = useState<CustomerUser[]>([]);
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [listError, setListError] = useState("");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [invitation, setInvitation] = useState<{ email: string; role: CustomerUserRole }>({
    email: "",
    role: "customer_user",
  });
  const [actionMessage, setActionMessage] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [userResult, invitationResult] = await Promise.all([
        apiClient<{ items: CustomerUser[] }>(CUSTOMER_USERS_PATH),
        apiClient<{ items: InvitationSummary[] }>(INVITATIONS_PATH),
      ]);
      setUsers(userResult.items);
      setInvitations(invitationResult.items);
      setListError("");
    } catch (error) {
      setListError(apiErrorMessage(error, "メンバー情報を取得できませんでした。"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const issueInvitation = async () => {
    setActionMessage("");
    setBusyId("invite");
    try {
      await apiClient(INVITATIONS_PATH, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          email: invitation.email,
          role: invitation.role,
          expiresInHours: 168,
        }),
      });
      setInvitation({ email: "", role: "customer_user" });
      setShowInviteForm(false);
      setActionMessage("招待メールを送信しました。招待された本人が承諾すると、組織のメンバーに追加されます。");
      await fetchData();
    } catch (error) {
      setActionMessage(membershipErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const mutateMember = async (user: CustomerUser, action: "role" | "disable" | "enable" | "delete", role?: CustomerUserRole) => {
    setBusyId(user.customerUserId);
    setActionMessage("");
    try {
      if (action === "role") {
        await apiClient(`${CUSTOMER_USERS_PATH}/${user.customerUserId}/role-changes`, {
          method: "POST",
          body: JSON.stringify({ role, expectedRole: user.role, expectedUserState: "active" }),
        });
      } else if (action === "delete") {
        await apiClient(`${CUSTOMER_USERS_PATH}/${user.customerUserId}`, {
          method: "DELETE",
          body: JSON.stringify({ expectedUserState: user.userState }),
        });
      } else {
        await apiClient(`${CUSTOMER_USERS_PATH}/${user.customerUserId}/${action}`, {
          method: "POST",
          body: JSON.stringify({ expectedUserState: user.userState }),
        });
      }
      await fetchData();
    } catch (error) {
      setActionMessage(membershipErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvitation = async (invitationId: string) => {
    setBusyId(invitationId);
    setActionMessage("");
    try {
      await apiClient(`${INVITATIONS_PATH}/${invitationId}/revoke`, { method: "POST" });
      await fetchData();
    } catch (error) {
      setActionMessage(membershipErrorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  if (isLoading) {
    return <div className="space-y-3 p-6">{[...Array(3)].map((_, index) =>
      <div key={index} className="h-12 animate-pulse rounded-md bg-muted" />)}</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("users.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">権限は管理者と一般ユーザーの2種類です。招待された本人が承諾すると、組織のメンバーに追加されます。</p>
        </div>
        <Button size="sm" onClick={() => setShowInviteForm((current) => !current)}>
          <MailPlus className="mr-2 h-4 w-4" />メンバーを招待
        </Button>
      </div>

      {showInviteForm ? <Card className="max-w-xl"><CardHeader><CardTitle className="text-base">組織へ招待</CardTitle></CardHeader><CardContent className="space-y-3">
        <div className="space-y-1"><Label htmlFor="invitation-email">メールアドレス</Label><Input id="invitation-email" type="email" value={invitation.email} onChange={(event) => setInvitation({ ...invitation, email: event.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="invitation-role">承諾後の権限</Label><select id="invitation-role" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={invitation.role} onChange={(event) => setInvitation({ ...invitation, role: event.target.value as CustomerUserRole })}><option value="customer_user">{t("users.roles.customer_user")}</option><option value="customer_admin">{t("users.roles.customer_admin")}</option></select></div>
        <p className="text-xs text-muted-foreground">招待コードはメール本文に別掲し、URLには含めません。有効期限は7日です。</p>
        <div className="flex gap-2"><Button variant="outline" onClick={() => setShowInviteForm(false)}>{tc("actions.cancel")}</Button><Button disabled={!invitation.email || busyId === "invite"} onClick={() => void issueInvitation()}>招待を送信</Button></div>
      </CardContent></Card> : null}

      {actionMessage ? <div role="status" className="rounded-md border bg-muted p-3 text-sm">{actionMessage}</div> : null}
      {listError ? <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{listError}</div> : null}

      <Card><CardHeader><CardTitle className="text-base">現在のメンバー</CardTitle></CardHeader><CardContent className="overflow-x-auto p-0"><table className="w-full text-sm"><thead><tr className="border-b bg-muted/50"><th className="px-4 py-3 text-left">表示名</th><th className="px-4 py-3 text-left">メール</th><th className="px-4 py-3 text-left">権限</th><th className="px-4 py-3 text-left">状態</th><th className="px-4 py-3 text-left">操作</th></tr></thead><tbody>
        {users.map((user) => <tr key={user.customerUserId} className="border-b"><td className="px-4 py-3 font-medium">{user.displayName}</td><td className="px-4 py-3">{user.email}</td><td className="px-4 py-3">{user.userState === "active" ? <select aria-label={`${user.displayName}の権限`} className="rounded-md border bg-background px-2 py-1" value={user.role} disabled={busyId === user.customerUserId} onChange={(event) => void mutateMember(user, "role", event.target.value as CustomerUserRole)}><option value="customer_user">{t("users.roles.customer_user")}</option><option value="customer_admin">{t("users.roles.customer_admin")}</option></select> : t(`users.roles.${user.role}`, user.role)}</td><td className="px-4 py-3"><StatusBadge state={user.userState} /></td><td className="px-4 py-3"><div className="flex flex-wrap gap-2">{user.userState === "active" ? <Button size="sm" variant="outline" disabled={busyId === user.customerUserId} onClick={() => void mutateMember(user, "disable")}>無効化</Button> : null}{user.userState === "disabled" ? <Button size="sm" variant="outline" disabled={busyId === user.customerUserId} onClick={() => void mutateMember(user, "enable")}>有効化</Button> : null}{user.userState !== "deleted" ? <Button size="sm" variant="destructive" disabled={busyId === user.customerUserId} onClick={() => void mutateMember(user, "delete")}>削除</Button> : null}</div></td></tr>)}
        {users.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">{tc("empty.noData")}</td></tr> : null}
      </tbody></table></CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">承諾待ちの招待</CardTitle></CardHeader><CardContent className="space-y-2">{invitations.map((item) => <div key={item.invitationId} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"><div><div className="font-medium">{item.maskedRecipientEmail}</div><div className="text-xs text-muted-foreground">{t(`users.roles.${item.role}`, item.role)}・期限 {new Date(item.expiresAt).toLocaleString("ja-JP")}・{item.invitationState}</div></div>{item.invitationState === "delivered" || item.invitationState === "delivery_unknown" ? <Button size="sm" variant="outline" disabled={busyId === item.invitationId} onClick={() => void revokeInvitation(item.invitationId)}>取り消す</Button> : null}</div>)}{invitations.length === 0 ? <p className="text-sm text-muted-foreground">承諾待ちの招待はありません。</p> : null}</CardContent></Card>
    </div>
  );
}

function membershipErrorMessage(error: unknown) {
  if (!(error instanceof ApiError)) return apiErrorMessage(error, "操作を完了できませんでした。");
  if (error.code === "last_active_customer_admin") return "有効な管理者を0人にはできません。先に別のメンバーを管理者にしてください。";
  if (error.code === "membership_current_ownership_remains") return "現在の所有設定が残っています。移譲してから再実行してください。";
  if (error.code === "membership_personal_credential_remains") return "個人資格情報が残っています。削除してから再実行してください。";
  if (error.code === "membership_state_conflict") return "メンバー状態が変わりました。再読み込みして確認してください。";
  if (error.code === "invitation_delivery_unknown") return "招待メールの配送結果を確認できません。自動再送はしていません。招待一覧を確認してください。";
  return apiErrorMessage(error, "操作を完了できませんでした。");
}
